import type { Dispatch } from 'redux';
import {
    contactsLeftNavItemId,
    createActionFactory,
    createDataActionFactory,
    Feature,
    OwsError,
    createThunk,
    ThunkDispatch,
} from '../../../shared';
import { importContactList } from '../../datalayer/importContactList';
import {
    refreshCurrentPersonaListData,
    getContactFoldersAndSubscribeToChanges,
    unsubscribeToChangesInAllContactFolders,
} from '../../../hub';
import { getAppState, getPackageState } from '../../../appstate';
import { ImportContactsState, packageStateKey } from '../../state';
import { getEncodingExamples, hasEncodingExamples } from '../../utils/encodingUtils';
import { chunkCSVBytes } from '../../utils/csvUtils';

export const importContactsLoadFileCompletedAction = createActionFactory<{
    csvBytes: Uint8Array;
    csvEncodingExamples: string[][];
}>('ImportContactsLoadFileCompletedAction', Feature.ImportContacts);

export const importContactsUploadFileRequestedAction = createActionFactory<{
    requestedCount: number;
    timestamp: number;
}>('ImportContactsUploadFileRequestedAction', Feature.ImportContacts);

export const importContactsUploadFileProgressAction = createActionFactory<{
    completedCount: number;
    timestamp: number;
}>('ImportContactsUploadFileProgressAction', Feature.ImportContacts);

export const importContactsUploadFileCompletedAction = createDataActionFactory<
    { importFolderId: string },
    { errorMessage: string }
>('ImportContactsUploadFileCompletedAction', Feature.ImportContacts);

export const maxEntriesPerCSV = 50;

const importContactsUploadFileRequested = (dispatch: Dispatch) => (requestedCount: number) =>
    dispatch(importContactsUploadFileRequestedAction({ requestedCount, timestamp: Date.now() }));

const importContactsUploadFileProgress = (dispatch: Dispatch) => (completedCount: number) =>
    dispatch(importContactsUploadFileProgressAction({ completedCount, timestamp: Date.now() }));

const importContactsUploadFileCompleted = (dispatch: Dispatch) => (importFolderId: string) => {
    dispatch(importContactsUploadFileCompletedAction.success({ importFolderId: importFolderId }));

    // Send signal back to parent window if it's opened via deeplink
    if (window.opener?.postMessage) {
        window.opener.postMessage('importContactsCompleted', window.location.origin);
    }
};

const importContactsUploadFileFailed = (dispatch: Dispatch) => (errorMessage: string) =>
    dispatch(importContactsUploadFileCompletedAction.error({ errorMessage }));

export const importContactsLoadFileCompleted = (dispatch: ThunkDispatch) => (
    csvArrayBuffer: ArrayBuffer
) => {
    const csvBytes = new Uint8Array(csvArrayBuffer, 0);
    getEncodingExamples(csvBytes).then((csvEncodingExamples: string[][]) => {
        dispatch(importContactsLoadFileCompletedAction({ csvBytes, csvEncodingExamples }));

        // If there are no encoding examples found, we jump straight to uploading the file
        if (!hasEncodingExamples(csvEncodingExamples)) {
            dispatch(importContactsUploadFile());
        }
    });
};

export const importContactsUploadFile = () =>
    createThunk(
        'ImportContactsUploadFileAction',
        (dispatch: ThunkDispatch, getState: () => {}): void => {
            unsubscribeToChangesInAllContactFolders(dispatch);

            const state = getPackageState<ImportContactsState>(getState(), packageStateKey);

            // Transform into list of Byte "chunks" with at most 50 lines each, to avoid overloading/timeout on backend
            const [chunkedCSVBytes, contactCount] = chunkCSVBytes(
                state.packageState.csvBytes,
                maxEntriesPerCSV
            );

            importContactsUploadFileRequested(dispatch)(contactCount);

            const processChunks = () => {
                if (!chunkedCSVBytes.length) {
                    const appState = getAppState(getState());
                    const defaultContactFolder =
                        appState.data.contactFoldersState.defaultContactFolder;
                    // Navigate to Your/All Contacts folder when CAPIv3 is enabled
                    const importFolderId = appState.view.layoutState.isCAPIv3Enabled
                        ? contactsLeftNavItemId
                        : defaultContactFolder?.id;
                    importContactsUploadFileCompleted(dispatch)(importFolderId);
                    dispatch(getContactFoldersAndSubscribeToChanges);
                    dispatch(refreshCurrentPersonaListData);
                    return;
                }

                const chunkBytes = chunkedCSVBytes.shift();
                importContactList(chunkBytes)
                    .then(importedCount => {
                        importContactsUploadFileProgress(dispatch)(importedCount);
                        processChunks();
                    })
                    .catch((error: OwsError) =>
                        importContactsUploadFileFailed(dispatch)(error.message || 'UnknownError')
                    );
            };

            processChunks();
        }
    );
export type { DataActionFactory } from '../../../shared';
export type { Action } from '../../../shared';
